import {
  checkinVersionLabel,
  checkinVersions,
  coproApprovalStatuses,
  coproApprovalStatusLabel,
  designStatuses,
  designStatusLabel,
  implementationTypologies,
  implementationTypologyLabel,
  launchClearanceStatuses,
  launchClearanceStatusLabel,
  leaseContractTypeLabel,
  leaseContractTypes,
  projectTypologies,
  projectTypologyLabel,
} from './enums'

export const STORE_PROJECT_API_STATUSES = [
  'before_project',
  'under_study',
  'advanced_project',
  'secured',
  'works',
  'openable',
  'open',
] as const

export type StoreProjectApiStatus = (typeof STORE_PROJECT_API_STATUSES)[number]

export const REAL_ESTATE_RESTRICTED_STATUSES: readonly StoreProjectApiStatus[] = [
  'before_project',
] as const

export const storeProjectStatusLabel: Record<StoreProjectApiStatus, string> = {
  before_project: 'Prez projet',
  under_study: 'Étude projet',
  advanced_project: 'Avant projet',
  secured: 'Projet sécurisé',
  works: 'Travaux',
  openable: 'Ouvrable',
  open: 'Ouvert',
}

export const STORE_PROJECT_FORM_SECTIONS = [
  'IDENTIFICATION',
  'BUDGET',
  'QUALITATIVE',
  'WORKS',
  'WORKS_COSTS',
  'LOGISTICS',
  'PROJECT',
  'SURFACE',
  'DESIGN_LAYOUT',
  'ADMIN_AUTHORIZATIONS',
] as const

export type StoreProjectFormSection = (typeof STORE_PROJECT_FORM_SECTIONS)[number]

export interface StoreProjectSelectOption {
  value: string
  label: string
}

export interface FieldAdornment {
  type: 'text' | 'icon'
  value: string
  position: 'start' | 'end'
}

export interface StoreProjectFieldDefinition {
  key: string
  label: string
  section: StoreProjectFormSection
  statuses: StoreProjectApiStatus[]
  required: boolean
  type:
    | 'text'
    | 'number'
    | 'currency'
    | 'date'
    | 'textarea'
    | 'select'
    | 'person-select'
    | 'admin-select'
  options?: StoreProjectSelectOption[]
  display?: 'full' | 'left' | 'right' | '1/3' | '2/3' | '3/3'
  adornment?: FieldAdornment
  /**
   * When true, the field value is auto-computed and cannot be edited by the user.
   * The computed value is managed externally (e.g. via AUTO_DISABLED_CONFIGS).
   */
  disabled?: true
}

const everyPlanningStatus: StoreProjectApiStatus[] = [
  'before_project',
  'under_study',
  'advanced_project',
  'secured',
]

const everyExecutionStatus: StoreProjectApiStatus[] = ['works', 'openable', 'open']

const fromUnderStudyToOpen: StoreProjectApiStatus[] = [
  'under_study',
  'advanced_project',
  'secured',
  'works',
  'openable',
  'open',
]

const allStatuses: StoreProjectApiStatus[] = [
  'before_project',
  'under_study',
  'advanced_project',
  'secured',
  'works',
  'openable',
  'open',
]

const fromSecuredToOpen: StoreProjectApiStatus[] = ['secured', 'works', 'openable', 'open']

const fromWorksToOpen: StoreProjectApiStatus[] = ['works', 'openable', 'open']

export const STORE_PROJECT_FIELDS: StoreProjectFieldDefinition[] = [
  // Identification
  {
    key: 'name',
    label: 'Nom du projet',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'text',
    display: '1/3',
    disabled: true,
  },
  {
    key: 'shortLabel',
    label: 'Nom du projet court',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: false,
    type: 'text',
    display: '1/3',
    disabled: true,
  },
  {
    key: 'status',
    label: 'Statut',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'select',
    options: STORE_PROJECT_API_STATUSES.map((value) => ({
      value,
      label: storeProjectStatusLabel[value],
    })),
    display: '2/3',
  },
  {
    key: 'id',
    label: 'ID du projet',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'text',
    display: '3/3',
    disabled: true,
  },
  {
    key: 'devId',
    label: 'ID dev',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'text',
    display: 'left',
    disabled: true,
  },
  {
    key: 'dev',
    label: 'Prénom Nom dev',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'admin-select',
    display: 'right',
  },
  {
    key: 'address',
    label: 'Adresse',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'text',
    adornment: { type: 'icon', value: 'map-pin', position: 'start' },
  },
  {
    key: 'postalCode',
    label: 'Code postal',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'text',
    display: 'left',
  },
  {
    key: 'city',
    label: 'Ville',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'text',
    display: 'right',
  },
  {
    key: 'googleMapsUrl',
    label: 'Lien Google Maps',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: false,
    type: 'text',
    display: 'left',
    adornment: { type: 'icon', value: 'link', position: 'start' },
    disabled: true,
  },
  {
    key: 'leaseData.totalFloorArea',
    label: 'SdP Totale',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'number',
    adornment: { type: 'text', value: 'm²', position: 'end' },
    display: 'right',
  },
  {
    key: 'aggloSize',
    label: "Taille de l'agglo",
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'number',
    display: 'left',
    disabled: true,
  },
  {
    key: 'leaseData.implementationTypology',
    label: "Typologie d'emplacement",
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: true,
    type: 'select',
    options: implementationTypologies.map((value) => ({
      value,
      label: implementationTypologyLabel[value],
    })),
    display: 'right',
  },
  {
    key: 'latitude',
    label: 'Latitude',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: false,
    type: 'number',
    display: 'left',
  },
  {
    key: 'longitude',
    label: 'Longitude',
    section: 'IDENTIFICATION',
    statuses: allStatuses,
    required: false,
    type: 'number',
    display: 'right',
  },

  // Budget
  {
    key: 'rentPerYear',
    label: 'Loyer annuel',
    section: 'BUDGET',
    statuses: allStatuses,
    required: true,
    type: 'currency',
    display: 'left',
    adornment: { type: 'text', value: '€', position: 'end' },
  },
  {
    key: 'works',
    label: 'Travaux',
    section: 'BUDGET',
    statuses: allStatuses,
    required: false,
    type: 'currency',
    display: 'right',
    adornment: { type: 'text', value: '€', position: 'end' },
    disabled: true,
  },
  {
    key: 'leaseFunds',
    label: 'Droit au bail / Fonds',
    section: 'BUDGET',
    statuses: allStatuses,
    required: false,
    type: 'currency',
    display: 'left',
    adornment: { type: 'text', value: '€', position: 'end' },
  },
  {
    key: 'otherCosts',
    label: 'Autres coûts',
    section: 'BUDGET',
    statuses: allStatuses,
    required: false,
    type: 'currency',
    adornment: { type: 'text', value: '€', position: 'end' },
    display: 'right',
    disabled: true,
  },

  // Qualitative
  {
    key: 'comment',
    label: 'Observations',
    section: 'QUALITATIVE',
    statuses: everyPlanningStatus,
    required: false,
    type: 'textarea',
  },

  // Works
  {
    key: 'worksData.workDurationWeeks',
    label: 'Durée des travaux (semaines)',
    section: 'WORKS',
    statuses: everyPlanningStatus,
    required: false,
    type: 'number',
  },
  {
    key: 'worksData.keyReceptionDate',
    label: 'Date de réception des clés',
    section: 'LOGISTICS',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
  },
  {
    key: 'worksData.workStartDate',
    label: 'Date début des travaux',
    section: 'WORKS',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
  },
  {
    key: 'worksData.workEndDate',
    label: 'Date fin de chantier',
    section: 'WORKS',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
  },
  {
    key: 'worksData.openingDate',
    label: "Date d'ouverture",
    section: 'LOGISTICS',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
  },
  {
    key: 'worksData.moeExecution',
    label: 'MOE exécution',
    section: 'WORKS',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'person-select',
    display: 'left',
  },
  {
    key: 'worksData.projectManagerAdminId',
    label: 'Chef de projet travaux',
    section: 'WORKS',
    statuses: fromWorksToOpen,
    required: false,
    type: 'admin-select',
    display: 'right',
  },
  {
    key: 'worksData.fitter',
    label: 'Agenceur',
    section: 'LOGISTICS',
    statuses: fromWorksToOpen,
    required: true,
    type: 'person-select',
    display: 'left',
  },
  {
    key: 'worksData.preliminaryVisitDate',
    label: 'Date prévisionnelle VT',
    section: 'LOGISTICS',
    statuses: fromWorksToOpen,
    required: true,
    type: 'date',
    display: 'right',
  },
  {
    key: 'worksData.technicalAuditTodoDate',
    label: 'Statut audit technique - À faire le',
    section: 'LOGISTICS',
    statuses: fromWorksToOpen,
    required: false,
    type: 'date',
  },
  {
    key: 'worksData.technicalAuditPlannedDate',
    label: 'Statut audit technique - Planifié le',
    section: 'LOGISTICS',
    statuses: fromWorksToOpen,
    required: false,
    type: 'date',
  },
  {
    key: 'worksData.technicalAuditDoneDate',
    label: 'Statut audit technique - Fait le',
    section: 'LOGISTICS',
    statuses: fromWorksToOpen,
    required: false,
    type: 'date',
  },
  {
    key: 'worksData.proDceTodoDate',
    label: 'Statut PRO/DCE - À faire le',
    section: 'LOGISTICS',
    statuses: fromWorksToOpen,
    required: false,
    type: 'date',
  },
  {
    key: 'worksData.proDceStartedDate',
    label: 'Statut PRO/DCE - Lancé le',
    section: 'LOGISTICS',
    statuses: fromWorksToOpen,
    required: false,
    type: 'date',
  },
  {
    key: 'worksData.proDceDoneDate',
    label: 'Statut PRO/DCE - Fini le',
    section: 'LOGISTICS',
    statuses: fromWorksToOpen,
    required: false,
    type: 'date',
  },
  {
    key: 'worksData.proDceTargetDate',
    label: 'Date PRO/DCE',
    section: 'LOGISTICS',
    statuses: fromWorksToOpen,
    required: true,
    type: 'date',
  },
  {
    key: 'worksData.validatedGoBudget',
    label: 'Montant projet validé',
    section: 'WORKS_COSTS',
    statuses: fromWorksToOpen,
    required: true,
    type: 'currency',
    display: 'left',
    adornment: { type: 'text', value: '€', position: 'end' },
  },
  {
    key: 'worksData.dgdBudget',
    label: 'Montant projet DGD',
    section: 'WORKS_COSTS',
    statuses: fromWorksToOpen,
    required: true,
    type: 'currency',
    display: 'right',
    adornment: { type: 'text', value: '€', position: 'end' },
  },

  // Land commitment
  {
    key: 'leaseData.contractType',
    label: 'Type de contrat',
    section: 'PROJECT',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'select',
    options: leaseContractTypes.map((value) => ({ value, label: leaseContractTypeLabel[value] })),
  },
  {
    key: 'leaseData.projectTypology',
    label: 'Typologie projet',
    section: 'PROJECT',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'select',
    options: projectTypologies.map((value) => ({ value, label: projectTypologyLabel[value] })),
  },
  {
    key: 'leaseData.loiValidationDate',
    label: 'Date validation LOI',
    section: 'PROJECT',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
    display: 'left',
  },
  {
    key: 'leaseData.promiseProjectedDate',
    label: 'Date prévisionnelle promesse',
    section: 'PROJECT',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
    display: 'right',
  },
  {
    key: 'leaseData.promiseSignatureDate',
    label: 'Date signature promesse',
    section: 'PROJECT',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
    display: 'left',
  },
  {
    key: 'leaseData.expectedSigningDate',
    label: 'Date prévisionnelle signature bail',
    section: 'PROJECT',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
    display: 'right',
  },
  {
    key: 'leaseData.signatureDate',
    label: 'Date signature bail',
    section: 'PROJECT',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
    display: 'left',
  },
  {
    key: 'leaseData.leaseStartDate',
    label: "Date de prise d'effet du bail",
    section: 'PROJECT',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'date',
    display: 'right',
  },
  {
    key: 'leaseData.coproApproval',
    label: 'Accord copro',
    section: 'PROJECT',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'select',
    options: coproApprovalStatuses.map((value) => ({
      value,
      label: coproApprovalStatusLabel[value],
    })),
  },
  {
    key: 'leaseData.totalFloorArea',
    label: 'Surface de plancher Total',
    section: 'SURFACE',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'number',
    adornment: { type: 'text', value: 'm²', position: 'end' },
  },
  {
    key: 'leaseData.salesFloorArea',
    label: 'Surface de plancher Vente',
    section: 'SURFACE',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'number',
    adornment: { type: 'text', value: 'm²', position: 'end' },
  },
  {
    key: 'leaseData.backOfficeFloorArea',
    label: 'Surface de plancher BO',
    section: 'SURFACE',
    statuses: fromUnderStudyToOpen,
    required: true,
    type: 'number',
    adornment: { type: 'text', value: 'm²', position: 'end' },
  },

  // Design layout
  {
    key: 'designData.designRequestDate',
    label: 'Date demande conception',
    section: 'DESIGN_LAYOUT',
    statuses: fromSecuredToOpen,
    required: true,
    type: 'date',
  },
  {
    key: 'designData.designStatus',
    label: 'Statut conception',
    section: 'DESIGN_LAYOUT',
    statuses: fromSecuredToOpen,
    required: true,
    type: 'select',
    options: designStatuses.map((value) => ({ value, label: designStatusLabel[value] })),
  },
  {
    key: 'designData.fridgeCountX',
    label: 'Nombre de frigos - X (125cm)',
    section: 'DESIGN_LAYOUT',
    statuses: fromSecuredToOpen,
    required: true,
    type: 'number',
  },
  {
    key: 'designData.fridgeCountY',
    label: 'Nombre de frigos - Y (188cm)',
    section: 'DESIGN_LAYOUT',
    statuses: fromSecuredToOpen,
    required: true,
    type: 'number',
  },
  {
    key: 'designData.fridgeCountZ',
    label: 'Nombre de frigos - Z (255cm)',
    section: 'DESIGN_LAYOUT',
    statuses: fromSecuredToOpen,
    required: true,
    type: 'number',
  },
  {
    key: 'designData.checkoutCount',
    label: 'Nombre de caisses',
    section: 'DESIGN_LAYOUT',
    statuses: fromSecuredToOpen,
    required: true,
    type: 'number',
  },
  {
    key: 'designData.checkinVersion',
    label: 'Version check-in',
    section: 'DESIGN_LAYOUT',
    statuses: fromSecuredToOpen,
    required: false,
    type: 'select',
    options: checkinVersions.map((version) => ({
      value: version,
      label: checkinVersionLabel[version],
    })),
  },
  {
    key: 'designData.validatedPlanUrl',
    label: 'V. de plans validée',
    section: 'DESIGN_LAYOUT',
    statuses: fromSecuredToOpen,
    required: true,
    type: 'text',
  },
  {
    key: 'designData.screen24Gourmandise',
    label: "Nombre d'écrans 24 pouces gourmandise",
    section: 'DESIGN_LAYOUT',
    statuses: fromUnderStudyToOpen,
    required: false,
    type: 'number',
  },
  {
    key: 'designData.screen26Gourmandise',
    label: "Nombre d'écrans 26 pouces gourmandise",
    section: 'DESIGN_LAYOUT',
    statuses: fromUnderStudyToOpen,
    required: false,
    type: 'number',
  },
  {
    key: 'designData.screen24Cafe',
    label: "Nombre d'écrans 24 pouces café",
    section: 'DESIGN_LAYOUT',
    statuses: fromUnderStudyToOpen,
    required: false,
    type: 'number',
  },
  {
    key: 'designData.screen26Cafe',
    label: "Nombre d'écrans 26 pouces café",
    section: 'DESIGN_LAYOUT',
    statuses: fromUnderStudyToOpen,
    required: false,
    type: 'number',
  },
  {
    key: 'designData.screen26Fridges',
    label: "Nombre d'écrans 26 pouces frigos",
    section: 'DESIGN_LAYOUT',
    statuses: fromUnderStudyToOpen,
    required: false,
    type: 'number',
  },
  {
    key: 'designData.externalScreens',
    label: "Nombre d'écrans extérieurs",
    section: 'DESIGN_LAYOUT',
    statuses: fromUnderStudyToOpen,
    required: false,
    type: 'number',
  },

  // Administrative authorizations
  {
    key: 'permitsData.designMoe',
    label: 'MOE conception (dépôts admin)',
    section: 'ADMIN_AUTHORIZATIONS',
    statuses: everyExecutionStatus,
    required: true,
    type: 'person-select',
  },
  {
    key: 'permitsData.dpFilingDate',
    label: 'Date dépôt DP',
    section: 'ADMIN_AUTHORIZATIONS',
    statuses: everyExecutionStatus,
    required: true,
    type: 'date',
  },
  {
    key: 'permitsData.dpObtainedDate',
    label: 'DP obtenue le',
    section: 'ADMIN_AUTHORIZATIONS',
    statuses: everyExecutionStatus,
    required: true,
    type: 'date',
  },
  {
    key: 'permitsData.atFilingDate',
    label: 'Date dépôt AT',
    section: 'ADMIN_AUTHORIZATIONS',
    statuses: everyExecutionStatus,
    required: true,
    type: 'date',
  },
  {
    key: 'permitsData.atObtainedDate',
    label: 'AT obtenue le',
    section: 'ADMIN_AUTHORIZATIONS',
    statuses: everyExecutionStatus,
    required: true,
    type: 'date',
  },
  {
    key: 'permitsData.deFilingDate',
    label: 'Date dépôt DE',
    section: 'ADMIN_AUTHORIZATIONS',
    statuses: everyExecutionStatus,
    required: true,
    type: 'date',
  },
  {
    key: 'permitsData.deObtainedDate',
    label: 'DE obtenue le',
    section: 'ADMIN_AUTHORIZATIONS',
    statuses: everyExecutionStatus,
    required: true,
    type: 'date',
  },
  {
    key: 'permitsData.launchClearance',
    label: 'Obtention des arrêtés avant démarrage',
    section: 'ADMIN_AUTHORIZATIONS',
    statuses: everyExecutionStatus,
    required: true,
    type: 'select',
    options: launchClearanceStatuses.map((value) => ({
      value,
      label: launchClearanceStatusLabel[value],
    })),
  },
]

export function getStoreProjectFieldsForStatus(
  status: StoreProjectApiStatus,
): StoreProjectFieldDefinition[] {
  return STORE_PROJECT_FIELDS.filter((field) => field.statuses.includes(status))
}

export function getStoreProjectRequiredFieldsForStatus(
  status: StoreProjectApiStatus,
): StoreProjectFieldDefinition[] {
  return getStoreProjectFieldsForStatus(status).filter((field) => field.required)
}
